/**
 * The demo's data quality: a per-tenant tracking baseline held in memory, and the
 * `data_quality` block the demo reports carry, built by the API's rules
 * (docs/architecture/data-quality.md). Before the baseline the demo has no tracked
 * visit at all — every order on those days is Not tracked, as on a real site whose
 * tracking started late — so a range reaching back before it reads as low coverage.
 */

import type { DataQuality, TrackingBaseline, TrackingBaselineInput } from '@/api/dataQuality'
import { BASELINE_NOTE_MAX_LENGTH } from '@/api/dataQuality'
import { ApiError } from '@/lib/api'
import { dayCount, toDateKey } from '@/lib/filters'

/** The days of `from`–`to` on or after `baseline`: all of them without one. */
export function trackedDays(from: string, to: string, baseline: string | null): number {
  const days = dayCount(from, to)
  if (baseline === null || baseline <= from) return days
  if (baseline > to) return 0
  return dayCount(baseline, to)
}

/** `tracked / orders` as the API writes it: a decimal string, two places, half-up; null with no orders. */
export function trackedShare(tracked: number, orders: number): string | null {
  if (orders <= 0) return null
  // Hundredths, rounded half-up in integers: no floating-point formatting is involved.
  const hundredths = Math.floor((tracked * 200 + orders) / (2 * orders))
  return `${Math.floor(hundredths / 100)}.${String(hundredths % 100).padStart(2, '0')}`
}

export interface DemoDataQualityInput {
  baseline: string | null
  from: string
  /** The comparison range's first day; null when the report does not compare. */
  previousFrom: string | null
  orders: number
  notTrackedOrders: number
}

/** The `data_quality` block for a demo report: `data_quality_block`'s rule, with coverage. */
export function demoDataQuality({ baseline, from, previousFrom, orders, notTrackedOrders }: DemoDataQualityInput): DataQuality {
  const tracked = Math.max(0, orders - notTrackedOrders)
  return {
    trackingReliableFrom: baseline,
    rangeBeforeBaseline: baseline !== null && from < baseline,
    comparisonBeforeBaseline: baseline !== null && previousFrom !== null && previousFrom < baseline,
    coverage: { orders, trackedOrders: tracked, notTrackedOrders: orders - tracked, trackedShare: trackedShare(tracked, orders) },
  }
}

/** A demo site's orders a day, and the share that can never carry a source (declined analytics, server-created orders). */
const SITE_ORDERS_PER_DAY = 16
const CONSENT_FLOOR = 0.17

/**
 * A demo site's orders over `from`–`to` and how many are Not tracked: the consent floor
 * on tracked days, every order before the baseline. For a demo report with no order
 * rows of its own to count (the Ads overview's TmT columns).
 */
export function demoSiteCoverage(from: string, to: string, baseline: string | null): { orders: number; notTrackedOrders: number } {
  const days = dayCount(from, to)
  const tracked = trackedDays(from, to, baseline)
  const orders = Math.round(SITE_ORDERS_PER_DAY * days)
  const notTrackedOrders = Math.round(SITE_ORDERS_PER_DAY * (days - tracked) + SITE_ORDERS_PER_DAY * tracked * CONSENT_FLOOR)
  return { orders, notTrackedOrders: Math.min(orders, notTrackedOrders) }
}

const UNSET: TrackingBaseline = { trackingReliableFrom: null, note: null, setBy: null, setAt: null }

/** Who set the demo's seeded baseline: a subject, as the API records it. */
const DEMO_SETTER = 'auth0|demo-owner'

/**
 * Each demo tenant's baseline, in memory for the client's lifetime, starting from the
 * seed the client derives from the tenant fixture (`trackingBaselineDaysAgo`).
 * `PUT` is refused as the API refuses it: without `settings.manage`, or a date after today.
 */
export class MockTrackingBaselineStore {
  private readonly baselines = new Map<string, TrackingBaseline>()

  constructor(private readonly now: () => Date) {}

  /** The tenant's baseline: what was last set, else `seed` (a date, set two days ago by the demo owner), else none. */
  get(tenantId: string, seed: string | null): TrackingBaseline {
    const stored = this.baselines.get(tenantId)
    if (stored) return stored
    if (seed === null) return UNSET
    return {
      trackingReliableFrom: seed,
      note: 'First full day of tracked visits after the connector upgrade.',
      setBy: DEMO_SETTER,
      setAt: new Date(this.now().getTime() - 2 * 86_400_000).toISOString(),
    }
  }

  set(tenantId: string, input: TrackingBaselineInput, subject: string, allowed: boolean): TrackingBaseline {
    if (!allowed) throw new ApiError(403, 'authorization_error', 'You do not have permission to change tenant settings.')
    const date = input.trackingReliableFrom
    if (date !== null && date > toDateKey(this.now())) {
      throw new ApiError(422, 'validation_error', 'The tracking baseline cannot be after today.')
    }
    const note = input.note?.trim() ?? ''
    if (note.length > BASELINE_NOTE_MAX_LENGTH) {
      throw new ApiError(422, 'validation_error', `The note must be at most ${BASELINE_NOTE_MAX_LENGTH} characters.`)
    }
    const baseline: TrackingBaseline = {
      trackingReliableFrom: date,
      note: note === '' ? null : note,
      setBy: subject,
      setAt: this.now().toISOString(),
    }
    this.baselines.set(tenantId, baseline)
    return baseline
  }
}
